import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const SecuritySettings = () => {
  const [settings, setSettings] = useState({
    passwordPolicy: {
      minLength: 8,
      requireUppercase: true,
      requireLowercase: true,
      requireNumbers: true,
      requireSpecialChars: true,
      passwordExpiry: 90
    },
    sessionSettings: {
      sessionTimeout: 30,
      maxConcurrentSessions: 3,
      requireReauth: true
    },
    loginSecurity: {
      maxFailedAttempts: 5,
      lockoutDuration: 30,
      enableTwoFactor: false,
      allowedIpRanges: '192.168.1.0/24'
    },
    auditSettings: {
      logRetention: 365,
      enableRealTimeAlerts: true,
      alertEmail: 'security@company.com'
    }
  });

  const [isLoading, setIsLoading] = useState(false);

  const handleSettingChange = (category, field, value) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev?.[category],
        [field]: value
      }
    }));
  };

  const handleSaveSettings = async () => {
    setIsLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      console.log('Security settings saved:', settings);
    } catch (error) {
      console.error('Error saving settings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const sessionTimeoutOptions = [
    { value: 15, label: '15 minutes' },
    { value: 30, label: '30 minutes' },
    { value: 60, label: '1 hour' },
    { value: 120, label: '2 hours' },
    { value: 480, label: '8 hours' }
  ];

  const lockoutDurationOptions = [
    { value: 15, label: '15 minutes' },
    { value: 30, label: '30 minutes' },
    { value: 60, label: '1 hour' },
    { value: 120, label: '2 hours' },
    { value: 1440, label: '24 hours' }
  ];

  const retentionOptions = [
    { value: 90, label: '90 days' },
    { value: 180, label: '6 months' },
    { value: 365, label: '1 year' },
    { value: 730, label: '2 years' },
    { value: 1825, label: '5 years' }
  ];

  return (
    <div className="space-y-6">
      {/* Password Policy */}
      <div className="bg-card rounded-lg border border-border shadow-maritime-sm">
        <div className="p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Lock" size={20} strokeWidth={2} className="text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Password Policy</h3>
              <p className="text-sm text-muted-foreground">Configure password requirements and expiration</p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Input
              label="Minimum Password Length"
              type="number"
              min="6"
              max="32"
              value={settings?.passwordPolicy?.minLength}
              onChange={(e) => handleSettingChange('passwordPolicy', 'minLength', parseInt(e?.target?.value))}
            />
            
            <Input
              label="Password Expiry (days)"
              type="number"
              min="30"
              max="365"
              value={settings?.passwordPolicy?.passwordExpiry}
              onChange={(e) => handleSettingChange('passwordPolicy', 'passwordExpiry', parseInt(e?.target?.value))}
            />
          </div>

          <div className="space-y-3">
            <h4 className="text-sm font-medium text-foreground">Password Requirements</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Checkbox
                label="Require uppercase letters"
                checked={settings?.passwordPolicy?.requireUppercase}
                onChange={(e) => handleSettingChange('passwordPolicy', 'requireUppercase', e?.target?.checked)}
              />
              <Checkbox
                label="Require lowercase letters"
                checked={settings?.passwordPolicy?.requireLowercase}
                onChange={(e) => handleSettingChange('passwordPolicy', 'requireLowercase', e?.target?.checked)}
              />
              <Checkbox
                label="Require numbers"
                checked={settings?.passwordPolicy?.requireNumbers}
                onChange={(e) => handleSettingChange('passwordPolicy', 'requireNumbers', e?.target?.checked)}
              />
              <Checkbox
                label="Require special characters"
                checked={settings?.passwordPolicy?.requireSpecialChars}
                onChange={(e) => handleSettingChange('passwordPolicy', 'requireSpecialChars', e?.target?.checked)}
              />
            </div>
          </div>
        </div>
      </div>
      {/* Session Management */}
      <div className="bg-card rounded-lg border border-border shadow-maritime-sm">
        <div className="p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
              <Icon name="Clock" size={20} strokeWidth={2} className="text-accent" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Session Management</h3>
              <p className="text-sm text-muted-foreground">Control user session behavior and timeouts</p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Select
              label="Session Timeout"
              options={sessionTimeoutOptions}
              value={settings?.sessionSettings?.sessionTimeout}
              onChange={(value) => handleSettingChange('sessionSettings', 'sessionTimeout', value)}
            />
            
            <Input
              label="Max Concurrent Sessions"
              type="number"
              min="1"
              max="10"
              value={settings?.sessionSettings?.maxConcurrentSessions}
              onChange={(e) => handleSettingChange('sessionSettings', 'maxConcurrentSessions', parseInt(e?.target?.value))}
            />
          </div>

          <Checkbox
            label="Require re-authentication for sensitive operations"
            description="Users must re-enter their password for critical actions"
            checked={settings?.sessionSettings?.requireReauth}
            onChange={(e) => handleSettingChange('sessionSettings', 'requireReauth', e?.target?.checked)}
          />
        </div>
      </div>
      {/* Login Security */}
      <div className="bg-card rounded-lg border border-border shadow-maritime-sm">
        <div className="p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
              <Icon name="Shield" size={20} strokeWidth={2} className="text-warning" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Login Security</h3>
              <p className="text-sm text-muted-foreground">Configure login protection and access controls</p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Input
              label="Max Failed Login Attempts"
              type="number"
              min="3"
              max="10"
              value={settings?.loginSecurity?.maxFailedAttempts}
              onChange={(e) => handleSettingChange('loginSecurity', 'maxFailedAttempts', parseInt(e?.target?.value))}
            />
            
            <Select
              label="Account Lockout Duration"
              options={lockoutDurationOptions}
              value={settings?.loginSecurity?.lockoutDuration}
              onChange={(value) => handleSettingChange('loginSecurity', 'lockoutDuration', value)}
            />
          </div>

          <Input
            label="Allowed IP Ranges"
            type="text"
            placeholder="192.168.1.0/24, 10.0.0.0/8"
            description="Comma-separated list of allowed IP ranges (CIDR notation)"
            value={settings?.loginSecurity?.allowedIpRanges}
            onChange={(e) => handleSettingChange('loginSecurity', 'allowedIpRanges', e?.target?.value)}
          />

          <Checkbox
            label="Enable Two-Factor Authentication"
            description="Require 2FA for all users (recommended for production)"
            checked={settings?.loginSecurity?.enableTwoFactor}
            onChange={(e) => handleSettingChange('loginSecurity', 'enableTwoFactor', e?.target?.checked)}
          />
        </div>
      </div>
      {/* Audit Settings */}
      <div className="bg-card rounded-lg border border-border shadow-maritime-sm">
        <div className="p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
              <Icon name="FileText" size={20} strokeWidth={2} className="text-success" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Audit & Logging</h3>
              <p className="text-sm text-muted-foreground">Configure audit trail and security monitoring</p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Select
              label="Log Retention Period"
              options={retentionOptions}
              value={settings?.auditSettings?.logRetention}
              onChange={(value) => handleSettingChange('auditSettings', 'logRetention', value)}
            />
            
            <Input
              label="Security Alert Email"
              type="email"
              placeholder="security@company.com"
              value={settings?.auditSettings?.alertEmail}
              onChange={(e) => handleSettingChange('auditSettings', 'alertEmail', e?.target?.value)}
            />
          </div>

          <Checkbox
            label="Enable Real-time Security Alerts"
            description="Send immediate notifications for suspicious activities"
            checked={settings?.auditSettings?.enableRealTimeAlerts}
            onChange={(e) => handleSettingChange('auditSettings', 'enableRealTimeAlerts', e?.target?.checked)}
          />
        </div>
      </div>
      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSaveSettings} loading={isLoading}>
          <Icon name="Save" size={16} strokeWidth={2} className="mr-2" />
          Save Security Settings
        </Button>
      </div>
    </div>
  );
};

export default SecuritySettings;